/**
 * Pure Wiki data helpers (no React, no network) — shared by the query hooks,
 * the TopBar crumb and the sections.
 *
 * Query keys — every Wiki read uses one of these so invalidation is reliable:
 *   ['wiki','categories']          GET /wiki/categories   (raw response cached)
 *   ['wiki','home']                GET /wiki/home
 *   ['wiki','counts']              GET /wiki/counts      ({acknowledge, brain_pending} nav counts)
 *   ['wiki','pages', params]       GET /wiki/pages?…
 *   ['wiki','page', id]            GET /wiki/pages/{id}   (the page doc; the TopBar crumb reads `title`)
 *   ['wiki','ack', ...rest]        GET /wiki/acknowledge…
 *   ['wiki','ask', ...rest]        Ask threads / turns
 *   ['wiki','brain', ...rest]      Brain tree / proposals / insights / settings
 */
export const wikiKeys = Object.freeze({
    all: () => ['wiki'],
    categories: () => ['wiki', 'categories'],
    // One category with its `access_summary` (GET /wiki/categories/{id}, #871);
    // ['wiki','category',id,'acl'] holds its access rules for Wiki admins.
    category: (id) => ['wiki', 'category', id],
    home: () => ['wiki', 'home'],
    counts: () => ['wiki', 'counts'],
    pages: (params = {}) => ['wiki', 'pages', params],
    page: (id) => ['wiki', 'page', id],
    ack: (...rest) => ['wiki', 'ack', ...rest],
    ask: (...rest) => ['wiki', 'ask', ...rest],
    brain: (...rest) => ['wiki', 'brain', ...rest],
    // The Archived view (#872): 'categories' | 'pages'.
    archived: (kind) => ['wiki', 'archived', kind],
});

// ── Categories ──────────────────────────────────────────────────────────────

function categoryRows(raw) {
    if (Array.isArray(raw)) return raw;
    const rows = raw?.categories || raw?.items || raw?.data || [];
    return Array.isArray(rows) ? rows : [];
}

function categoryId(row) {
    return row?.category_id || row?.id || '';
}

function byPosition(a, b) {
    const pa = typeof a.position === 'number' ? a.position : Number.MAX_SAFE_INTEGER;
    const pb = typeof b.position === 'number' ? b.position : Number.MAX_SAFE_INTEGER;
    return pa - pb;
}

/**
 * Index a `GET /wiki/categories` response. Accepts the API's flat list
 * (`{categories:[…]}` with `parent_id`) or an already-nested list
 * (`children`/`subcategories`/`subs`). Returns:
 *   tree  top-level categories in position order, each `{ ...row, id, subs: [...] }`
 *         (`children` is the same array, for older callers)
 *   flat  every category (top-level and sub), each with `id`
 *   byId  Map id → row
 * A subcategory whose parent is not visible to the caller is dropped.
 */
export function indexWikiCategories(raw) {
    const flat = [];
    const visit = (row, parentId) => {
        if (!row || typeof row !== 'object') return;
        const id = categoryId(row);
        if (!id) return;
        const nested = row.subs || row.subcategories || row.children;
        const item = { ...row, id, parent_id: row.parent_id ?? parentId ?? null };
        delete item.subs;
        delete item.subcategories;
        delete item.children;
        flat.push(item);
        if (Array.isArray(nested)) nested.forEach((child) => visit(child, id));
    };
    categoryRows(raw).forEach((row) => visit(row, null));

    const tops = flat.filter((row) => !row.parent_id).sort(byPosition);
    const topIds = new Set(tops.map((row) => row.id));
    // One level of subcategories only; a row whose parent is hidden (or is
    // itself a subcategory) has nowhere to render and is dropped everywhere.
    const visible = flat.filter((row) => !row.parent_id || topIds.has(row.parent_id));
    const byId = new Map(visible.map((row) => [row.id, row]));
    const tree = tops.map((top) => {
        const subs = visible.filter((row) => row.parent_id === top.id).sort(byPosition);
        return { ...top, subs, children: subs };
    });
    return { tree, flat: visible, byId };
}

/**
 * Resolve a category-or-subcategory id: `{ category, parent, isSub }` or null.
 * For a subcategory, `parent` is its top-level category.
 */
export function findWikiCategory(index, id) {
    if (!index || !id) return null;
    const category = index.byId.get(id);
    if (!category) return null;
    const parent = category.parent_id ? index.byId.get(category.parent_id) || null : null;
    return { category, parent, isSub: Boolean(parent) };
}
